/**
 * useSEO — Phase J1.21 (Fév 2026)
 *
 * Hook léger pour mettre à jour dynamiquement le <title>, <meta name="description"/>,
 * et <meta name="robots"/> côté React, sans dépendance externe (pas de react-helmet).
 *
 * Pourquoi : SEO audit Semrush a remonté
 *   1. Title tag dupliqué sur 3 pages
 *   2. Meta descriptions identiques
 *   3. Espaces privés (/menu-moi, /menu-salarie) indexés par erreur
 *
 * Usage :
 *   useSEO({
 *     title: "Devis électricien Perpignan · YouElec",
 *     description: "…",
 *     noindex: true,             // pour /menu-moi, /menu-salarie, /chantier, /client, /infra
 *     canonical: "https://nourlink.fr/",
 *   });
 *
 * Restaure automatiquement les valeurs précédentes au démontage.
 */
import { useEffect } from "react";

function setMeta(name, content, attr = "name") {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  const created = !el;
  if (created) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, prev, created };
}

function setCanonical(href) {
  if (!href) return null;
  let el = document.head.querySelector('link[rel="canonical"]');
  const created = !el;
  if (created) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, prev, created };
}

export default function useSEO({ title, description, noindex = false, canonical } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const descChange = description ? setMeta("description", description) : null;
    const robotsChange = setMeta(
      "robots",
      noindex ? "noindex, nofollow" : "index, follow"
    );
    const canonChange = canonical ? setCanonical(canonical) : null;

    return () => {
      document.title = prevTitle;
      if (descChange) {
        if (descChange.created) descChange.el.remove();
        else if (descChange.prev !== null) descChange.el.setAttribute("content", descChange.prev);
      }
      if (robotsChange) {
        if (robotsChange.created) robotsChange.el.remove();
        else if (robotsChange.prev !== null) robotsChange.el.setAttribute("content", robotsChange.prev);
      }
      if (canonChange) {
        if (canonChange.created) canonChange.el.remove();
        else if (canonChange.prev !== null) canonChange.el.setAttribute("href", canonChange.prev);
      }
    };
  }, [title, description, noindex, canonical]);
}
